/*
 * Market registry — the single source of truth for everything that differs
 * between comtev.com (global/UK) and comtev.ng (Nigeria).
 *
 * WHY COMMONJS, AND WHY IT LIVES IN src/
 * This file is read by BOTH the CRA bundle (via `src/config/index.ts`) and the
 * Node build scripts (`scripts/apply-market.js`, `generate-seo-files.js`,
 * `prerender.js`, `verify-market.js`). CRA's ModuleScopePlugin forbids the
 * bundle importing from outside `src/`, but Node's `require()` has no such
 * limit — so `src/` is the only location both can reach. Types live in the
 * hand-written `markets.d.ts` sibling.
 *
 * KEEP THIS FILE DEPENDENCY-FREE. It must contain no `require()` calls:
 * `@typescript-eslint/no-var-requires` is on and `npm run lint` covers .js.
 * It is pure data plus pure functions, and it must stay that way.
 *
 * The build selects a market with REACT_APP_MARKET (see scripts/resolve-market.js,
 * which validates the value and fails the build on anything unrecognised).
 */

const DEFAULT_MARKET = 'global';

/* Shared across markets — one definition so the two records cannot drift. */
const SAME_AS = [
    'https://x.com/comtevltd',
    'https://www.instagram.com/comtev_official/',
    'https://www.linkedin.com/company/comtev/about/',
];

/*
 * hreflang gate. While `live` is false the OTHER market's alternate link is not
 * emitted by either build — so comtev.com never publishes an alternate pointing
 * at a domain that does not resolve yet, and comtev.ng does not claim a pair
 * before it is indexable. Flip BOTH markets in the same commit and rebuild BOTH
 * images together: a reciprocal annotation is only valid when both sides
 * publish it, so a one-sided flip is worse than no flip at all.
 */
const ALTERNATES_LIVE = false;

const MARKETS = {
    global: {
        id: 'global',
        siteUrl: 'https://comtev.com',
        htmlLang: 'en',
        contentLanguage: 'en',
        hreflang: 'en-GB',
        ogLocale: 'en_GB',
        /* Exactly one market carries x-default. It is the fallback Google shows
           when no hreflang matches the searcher, so it belongs on the older,
           higher-authority domain. */
        xDefault: true,
        alternates: [{ market: 'ng', hreflang: 'en-NG', live: ALTERNATES_LIVE }],

        currency: { code: 'GBP', symbol: '£', locale: 'en-GB' },

        /* Self-serve plans, used by the pricing page copy and the
           SoftwareApplication AggregateOffer: a free plan, then Starter £79,
           Team £199 and Business £499 a month, every plan with AI credits
           included. These mirror the api's plan catalogue (credits
           DEFAULT_PLANS); change them together. `null` means the market has
           no published plans and the `offers` node is omitted — see
           markets.d.ts. */
        pricing: {
            plans: {
                priceCurrency: 'GBP',
                freeTier: true,
                fromPaidMonthly: 79,
                highMonthly: 499,
                offerCount: 4,
            },
        },

        org: {
            addresses: [
                {
                    '@type': 'PostalAddress',
                    addressLocality: 'Bushey',
                    addressRegion: 'Hertfordshire',
                    addressCountry: 'GB',
                },
            ],
            areaServed: ['United Kingdom'],
            sameAs: SAME_AS,
        },

        contact: {
            support: 'support@comtev.com',
            partners: 'partners@comtev.com',
            privacy: 'privacy@comtev.com',
            sales: 'abdul@comtev.com',
        },

        legal: {
            entityName: 'Comtev',
            localityLine: 'Bushey, Hertfordshire, United Kingdom',
        },

        /* Key into src/data/demoFigures.js. The demo numbers are market DATA
           requiring sign-off, never an FX conversion of the other market's. */
        demoFigures: 'gbp',

        /* Marker appended to app signup links (?src=...). comtev.com signs
           UK companies up: the marker makes the app force a COMPANY account
           (the ng marker forces EVENT_PLANNER and applies the work-email
           policy) — see the api's app.signup_policy. */
        signupSource: 'uk_company',

        /* Strings the static head in public/index.html bakes into EVERY page of
           the build (prerender snapshots the head). They were written for the
           corporate product, so each market must supply its own or comtev.ng
           ships "priced in pounds" on a page that says "free". Applied by
           scripts/apply-market.js. */
        head: {
            orgDescription:
                'Comtev gives event teams an AI that does the legwork: it keeps one living event document, sources and negotiates venues, vendors and travel, and chases every stakeholder decision, so the team decides and the event gets settled. Comtev is an AI corporate event planning platform built in the UK and priced in pounds; it also runs the guest list, RSVPs and QR-coded check-in.',
            webPageName: 'Comtev — AI Corporate Event Planning Software',
            ogImageAlt: 'Comtev — AI corporate event planning',
        },

        /* Top-level JSON-LD blocks in public/index.html's static head that this
           market must NOT publish. See the `ng` entry for why this exists. */
        dropStaticJsonLd: [],
    },

    ng: {
        id: 'ng',
        siteUrl: 'https://comtev.ng',
        htmlLang: 'en',
        contentLanguage: 'en',
        hreflang: 'en-NG',
        ogLocale: 'en_NG',
        xDefault: false,
        alternates: [{ market: 'global', hreflang: 'en-GB', live: ALTERNATES_LIVE }],

        currency: { code: 'NGN', symbol: '₦', locale: 'en-NG' },

        /* Nigeria is FREE TO RUN, so there are no plans to advertise and
           `null` is the correct, honest value. The planner pays nothing to open
           an account or run events; they buy message sends and guest-collection
           seats at wholesale and resell them to their client at a price they set
           themselves. Consumers treat `null` as "publish no figure": PricingPage
           omits the JSON-LD `offers` node entirely rather than emitting a
           placeholder.

           Do NOT put a number here to represent the wholesale rate. That is a
           per-service rate, not a headline product price, and publishing it as
           one would misdescribe the offer. The only approved Naira figures live
           in the worked example in src/data/pricingTiers.ts. */
        pricing: {
            plans: null,
        },

        org: {
            addresses: [
                {
                    '@type': 'PostalAddress',
                    streetAddress: '36 Ipodo Street',
                    addressLocality: 'Ikeja',
                    addressRegion: 'Lagos',
                    addressCountry: 'NG',
                },
            ],
            areaServed: ['Nigeria'],
            sameAs: SAME_AS,
        },

        contact: {
            support: 'support@comtev.com',
            partners: 'partners@comtev.com',
            privacy: 'privacy@comtev.com',
            sales: 'abdul@comtev.com',
        },

        legal: {
            entityName: 'Comtev',
            localityLine: 'Ikeja, Lagos, Nigeria',
        },

        demoFigures: 'ngn',

        /* comtev.ng signs planners up: the marker makes the app force an
           EVENT_PLANNER account and require a work email. */
        signupSource: 'ng_planner',

        /* See the global entry. Claims here are the owner-confirmed NG set
           (2026-08-18) and nothing else; no em dashes in NG copy. */
        head: {
            orgDescription:
                'Comtev builds event software. On comtev.ng it is free for event planners: an AI assistant collects the guest list on WhatsApp, invitations and QR access passes go out in one tap, door staff scan guests in, automations handle the follow-ups, and the planner invoices the client in naira and is paid through Paystack.',
            webPageName: 'Comtev, free event software for event planners',
            ogImageAlt: 'Comtev, free event software for event planners',
        },

        /* The static head in public/index.html was written for the corporate
           product, and prerender bakes it into EVERY page. On comtev.ng that
           shipped the do-not-claim list in machine-readable form: a
           SoftwareApplication whose featureList is venue/vendor/travel sourcing
           priced in GBP, a FAQPage answering corporate questions, and a HowTo
           describing a briefing flow planner accounts do not have. An answer
           engine reads that before it reads the page.

           These three are therefore dropped for Nigeria. Nothing is lost: the NG
           pages emit their own correct SoftwareApplication and FAQPage through
           usePageMeta, so the static ones were duplicative as well as wrong. The
           identity blocks (Organization, WebSite) and the structural ones stay,
           and apply-market.js already rewrites those per market. */
        dropStaticJsonLd: ['SoftwareApplication', 'FAQPage', 'HowTo'],
    },
};

const MARKET_IDS = Object.keys(MARKETS);

/*
 * Resolve a market id to its record, falling back to `global`.
 *
 * The fallback is deliberate: throwing here would run at module-init inside the
 * bundle and render a white screen. Validation belongs in the build, where it
 * can fail loudly and early — scripts/resolve-market.js rejects an unrecognised
 * REACT_APP_MARKET before webpack starts, so the fallback is unreachable in any
 * real build.
 */
function resolveMarket(value) {
    return MARKETS[value] || MARKETS[DEFAULT_MARKET];
}

/*
 * The hreflang set a given market publishes for one path. Returns self plus any
 * live alternates plus x-default, as {hreflang, href} pairs.
 *
 * `path` is an absolute route path ('/', '/pricing'). Callers that need a
 * cross-domain canonical (the blog) should NOT emit alternates at all — a page
 * that points its canonical elsewhere must not also claim to be one side of a
 * language pair.
 */
function alternatesFor(marketId, path) {
    const self = resolveMarket(marketId);
    const suffix = path === '/' ? '/' : path;
    const links = [{ hreflang: self.hreflang, href: `${self.siteUrl}${suffix}` }];

    self.alternates
        .filter((alt) => alt.live)
        .forEach((alt) => {
            const other = MARKETS[alt.market];
            if (other) links.push({ hreflang: alt.hreflang, href: `${other.siteUrl}${suffix}` });
        });

    const fallback = MARKET_IDS.map((id) => MARKETS[id]).find((m) => m.xDefault);
    /* x-default only means something once both sides are live; before that it
       would point searchers at a domain that has no counterpart. */
    if (fallback && links.length > 1) {
        links.push({ hreflang: 'x-default', href: `${fallback.siteUrl}${suffix}` });
    }

    return links;
}

module.exports = {
    DEFAULT_MARKET,
    MARKET_IDS,
    MARKETS,
    resolveMarket,
    alternatesFor,
};
